import { memo, type PropsWithChildren } from 'react';

import { FileIcon, X } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { Avatar, AvatarImage, Button, Icon } from '@/_components';

import { OverLay, type OverlayProps } from './overlay';

import type { ImageShape } from '../type';

export type ImagePreviewProps = Pick<OverlayProps, 'type'> & {
  callback: () => void;
  src: string;
  className?: string;
  disabled?: boolean;
  dimensions?: string;
  hideIcon?: boolean;
  shape: ImageShape;
  addPadding?: boolean;
  allowRemove?: boolean;
  isModal?: boolean;
  handleRemove?: () => void;
};

function PreviewBase({
  callback,
  src,
  children,
  className,
  disabled,
  shape = 'square',
  hideIcon = true,
  dimensions,
  addPadding,
  allowRemove = false,
  type,
  isModal = false,
  handleRemove,
}: PropsWithChildren<ImagePreviewProps>) {
  const round = shape === 'circle' ? 'rounded-full' : 'rounded-xl';
  const roundImage = shape === 'circle' ? 'rounded-full' : 'rounded-lg';
  const style = twMerge(
    'border-c-border-muted bg-c-avatar! relative flex border-2',
    className,
    dimensions,
    round,
    addPadding ? '' : ''
  );

  const renderThumb = () => {
    if (type === 'file' && src) {
      return (
        <a
          href={src}
          target="_blank"
          aria-label="View File"
          className={twMerge(
            'flex h-full w-full items-center justify-center',
            isModal ? 'z-10' : 'pointer-events-none'
          )}
          rel="noreferrer"
        >
          <Icon icon={FileIcon} className="size-7" />
        </a>
      );
    }

    if (src) {
      return (
        <AvatarImage src={src} className={twMerge('object-contain', roundImage, 'dark:transparent bg-transparent')} />
      );
    }

    return null;
  };

  return (
    <article className="flex flex-col items-center justify-center gap-2 text-center">
      <Avatar className={style}>
        <OverLay disabled={disabled} hasImage={!!src} hideIcon={hideIcon} onClick={callback} type={type} />
        {renderThumb()}
        {handleRemove && allowRemove && !disabled && (
          <Button
            as="contained"
            tag="div"
            className="absolute! top-1 right-1 z-30 rounded-full bg-black p-0.5 dark:bg-white"
            onClick={handleRemove}
          >
            <Icon icon={X} className="size-3 text-white dark:text-black" />
          </Button>
        )}
      </Avatar>

      {children}
    </article>
  );
}

export const Preview = memo(PreviewBase);
